// src/services/employeeService.js
//
// Employee service — v2.0 single source of truth for employee writes.
//
// All writes route through `createEmployee` (new) and `saveEmployee` (existing
// employee updates). Both helpers split the input by tier and write basic +
// PII docs in an atomic batch:
//
//   - Basic doc:  companies/{cid}/employees/{empId}
//                 — identity, employment (minus salary), schedule, status,
//                 contributions, hasPii flag
//   - PII doc:    companies/{cid}/employees/{empId}/pii/{empId}
//                 — banking, government IDs, salary (salaryType, dailySalary,
//                 monthlySalary)
//
// Tier-routing is config-driven via `PII_TOP_LEVEL_KEYS` and
// `PII_COMPENSATION_KEYS` — adding a new PII field is a one-line change here.
//
// See PRD_UAM_CustomRoles2.md §5.2 (field split) and §5.5 (service contract).

import {
  doc,
  collection,
  serverTimestamp,
  writeBatch,
  deleteField,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/config";

/**
 * Thrown when a create would produce a second employee document sharing an
 * existing 4-digit `employeeId` within the same company. `employeeId` is the
 * key DTRs (`dailyTimeRecords/{YYYY-MM_employeeId}`) and payroll select/compute
 * off, so it must be unique per company. Callers branch on `code` to show a
 * friendly message instead of a raw save error.
 */
export class DuplicateEmployeeIdError extends Error {
  constructor(employeeId) {
    super(`Employee ID ${employeeId} is already in use`);
    this.name = "DuplicateEmployeeIdError";
    this.code = "duplicate-employee-id";
    this.employeeId = employeeId;
  }
}

/**
 * True when an employee in the company already carries `employeeId`. This is
 * the single source of the "what counts as a duplicate" rule — no
 * status/`isDeleted` filter, because DTRs (`dailyTimeRecords/{YYYY-MM_employeeId}`)
 * persist across every status, so reuse of a resigned/terminated ID entangles
 * just as badly as an active one. Used by both the Step-1 UX check and the
 * authoritative save-layer guard so they can never disagree.
 */
export async function isEmployeeIdTaken(companyId, employeeId) {
  if (!companyId || !employeeId) {
    throw new Error("companyId and employeeId are required for the uniqueness check");
  }
  const snap = await getDocs(
    query(
      collection(db, "companies", companyId, "employees"),
      where("role", "==", "employee"),
      where("employeeId", "==", employeeId),
    ),
  );
  return !snap.empty;
}

/** Throw DuplicateEmployeeIdError if `employeeId` is already in use. */
export async function assertEmployeeIdUnique(companyId, employeeId) {
  if (await isEmployeeIdTaken(companyId, employeeId)) {
    throw new DuplicateEmployeeIdError(employeeId);
  }
}

// ============================================================
// CONFIG — extend here when PII fields grow
// ============================================================

/** Top-level keys that route entirely to the PII subcollection. */
export const PII_TOP_LEVEL_KEYS = ["banking", "governmentIds"];

/**
 * Keys under `employment.*` that move to the PII subcollection's
 * `compensation` block. Everything else under `employment` stays on the
 * basic doc — including contributions (sssContribution, etc.), which the
 * payroll deductions step reads live (as the monthly target) without
 * `emp:pii:view`. (They are no longer snapshotted onto DTR docs — see
 * PRD_RemoveDtrContributionSnapshot.)
 */
export const PII_COMPENSATION_KEYS = [
  "salaryType",
  "dailySalary",
  "monthlySalary",
  // PRD_CbaPremiumBase C3. Sits beside the salary it re-bases and inherits the
  // same emp:pii gating. WITHOUT this key the flag is written to the basic
  // employee document instead — and the client read is a blanket spread
  // (useEmployee.js), so the wizard would display it correctly either way and
  // the mistake would never surface in the UI.
  "cbaPremiumBase",
];

// ============================================================
// HELPERS
// ============================================================

/**
 * Split a flat employee data object into { basicData, piiData } payloads.
 *
 * Two input styles are supported and may be mixed in the same payload:
 *
 *   1. **Dotted-path keys** (preferred for updates) — e.g.
 *      `"employment.department"`, `"employment.salaryType"`. Firestore
 *      `update()` interprets these as nested-field paths and only
 *      modifies the listed fields on write — preserving every other
 *      field in the parent map. This is the right shape for surgical
 *      updates of legacy employees, where the parent doc may carry
 *      fields (compensationHistory, scheduledChanges, etc.) we don't
 *      want to inadvertently delete via object replacement.
 *
 *   2. **Whole object** under `employment` (used by createEmployee for
 *      new docs, where there's no existing data to preserve). This
 *      style writes the full `employment` map atomically.
 *
 * Routing:
 *   - banking, governmentIds                  → piiData (top-level)
 *   - employment.salaryType / dailySalary /   → piiData.compensation
 *     monthlySalary (either input style)
 *   - everything else under employment        → basicData.employment
 *     (object style) or basicData["employment.field"] (dotted style)
 *   - all other top-level keys                → basicData
 *
 * @param {object} input - Flat employee shape (object or dotted-path).
 * @returns {{ basicData: object, piiData: object }}
 */
export function splitEmployeeData(input) {
  const basicData = {};
  const piiData = {};

  for (const [key, value] of Object.entries(input)) {
    // Top-level PII keys (banking, governmentIds) — whole object to PII.
    if (PII_TOP_LEVEL_KEYS.includes(key)) {
      piiData[key] = value;
      continue;
    }

    // Dotted-path key under employment — route salary fields to PII
    // compensation, everything else stays as a dotted-path basic update.
    if (key.startsWith("employment.")) {
      const subKey = key.slice("employment.".length);
      if (PII_COMPENSATION_KEYS.includes(subKey)) {
        if (!piiData.compensation) piiData.compensation = {};
        piiData.compensation[subKey] = value;
      } else {
        basicData[key] = value;
      }
      continue;
    }

    // Whole-object employment — split nested keys by tier.
    if (key === "employment" && value && typeof value === "object") {
      const employment = {};
      const compensation = {};

      for (const [empKey, empValue] of Object.entries(value)) {
        if (PII_COMPENSATION_KEYS.includes(empKey)) {
          compensation[empKey] = empValue;
        } else {
          employment[empKey] = empValue;
        }
      }

      basicData.employment = employment;
      if (Object.keys(compensation).length > 0) {
        piiData.compensation = compensation;
      }
      continue;
    }

    basicData[key] = value;
  }

  return { basicData, piiData };
}

/**
 * True when PII data has all fields required for payroll eligibility.
 * Used to set the `hasPii` flag on the basic doc — Process Payroll filters
 * on this flag (`where('hasPii', '==', true)`) to exclude employees missing
 * the data needed to compute their pay.
 *
 * Required: salary type + amount + bank account + at least one government ID.
 *
 * @param {object} piiData - Full PII payload (or the merged result of
 *   existing PII + incoming updates). Partial updates may produce false
 *   negatives — see PRD §5.5 for the merge note.
 * @returns {boolean}
 */
export function piiHasRequiredFields(piiData) {
  if (!piiData) return false;
  const hasSalaryType = !!piiData.compensation?.salaryType;
  const hasSalaryAmount =
    Number(piiData.compensation?.dailySalary) > 0 ||
    Number(piiData.compensation?.monthlySalary) > 0;
  const hasBanking = !!piiData.banking?.accountNumber;
  const hasGovId = Object.values(piiData.governmentIds || {}).some(Boolean);
  return hasSalaryType && hasSalaryAmount && hasBanking && hasGovId;
}

// ============================================================
// PUBLIC API — createEmployee / saveEmployee
// ============================================================

/**
 * Create a new employee with split-collection structure.
 *
 * Atomic batch:
 *   1. Set basic doc (with `hasPii` flag computed from PII data)
 *   2. Set PII doc (only if any PII fields present)
 *   3. Set audit-trail entry (only if `auditEntry` provided)
 *
 * @param {string} companyId
 * @param {string} empId - The new employee's document ID
 * @param {object} employeeData - Full flat shape (basic + PII fields embedded
 *   as the form would submit them: banking, governmentIds at top level;
 *   salary fields nested under employment).
 * @param {object} [auditEntry] - Optional audit entry. The service adds the
 *   `timestamp` field; caller provides `action`, `userId`, `userEmail`,
 *   `userName`, `changes`, `metadata`.
 * @returns {Promise<void>}
 */
export async function createEmployee(companyId, empId, employeeData, auditEntry) {
  // Enforce employeeId uniqueness before the write. Note: `empId` is the random
  // Firestore document ID — the 4-digit business ID lives on
  // employeeData.employeeId, which is what must be unique.
  await assertEmployeeIdUnique(companyId, employeeData.employeeId);

  const { basicData, piiData } = splitEmployeeData(employeeData);
  const hasPii = piiHasRequiredFields(piiData);

  const basicRef = doc(db, "companies", companyId, "employees", empId);
  const piiRef = doc(basicRef, "pii", empId);

  const batch = writeBatch(db);

  // 1. Basic doc (with hasPii flag).
  batch.set(basicRef, { ...basicData, hasPii });

  // 2. PII doc (only if data present). Empty piiData means a basic-only
  //    employee — the `hasPii: false` flag tells the rest of the system.
  if (Object.keys(piiData).length > 0) {
    batch.set(piiRef, {
      ...piiData,
      employeeId: empId,
      metadata: {
        createdAt: serverTimestamp(),
        version: 1,
      },
    });
  }

  // 3. Audit trail entry.
  if (auditEntry) {
    const auditRef = doc(collection(basicRef, "auditTrail"));
    batch.set(auditRef, {
      timestamp: serverTimestamp(),
      ...auditEntry,
    });
  }

  await batch.commit();
}

/**
 * Update an existing employee. Splits updates by tier, writes atomically.
 *
 * Atomic batch:
 *   1. Update basic doc (with new `hasPii` flag if PII changed)
 *   2. Set-merge PII doc (only if PII fields in updates)
 *   3. Set audit-trail entry (only if `auditEntry` provided)
 *
 * **Limitation**: `piiHasRequiredFields` is evaluated against the incoming
 * `updates` only — not the merged result of existing PII + updates. Callers
 * should pass full PII data when updating PII fields, not partial updates.
 * The wizard always submits full form data, so this is safe in v2.0. Partial
 * PII updates (e.g. a future Payroll Specialist editing only salary) need
 * to read the existing PII doc first and pass the merged shape, or the
 * helper's hasPii calculation needs upgrading to a runTransaction.
 *
 * @param {string} companyId
 * @param {string} empId
 * @param {object} updates - Flat updates with mixed paths
 * @param {object} [auditEntry]
 */
export async function saveEmployee(companyId, empId, updates, auditEntry) {
  const { basicData, piiData } = splitEmployeeData(updates);

  const basicRef = doc(db, "companies", companyId, "employees", empId);
  const piiRef = doc(basicRef, "pii", empId);

  // Lazy migration: when PII fields are being written, also remove the
  // matching legacy fields from the parent doc. Pre-v2.0 employees carry
  // banking/governmentIds at the top level and salary fields inside the
  // employment map; this sweeps them off on first edit by a PII-having
  // user. Subsequent reads through useEmployee return the PII version.
  if (Object.keys(piiData).length > 0) {
    // Top-level PII keys (banking, governmentIds) on parent → delete.
    for (const key of PII_TOP_LEVEL_KEYS) {
      if (key in piiData) {
        basicData[key] = deleteField();
      }
    }
    // employment.salaryType / dailySalary / monthlySalary on parent → delete
    // (one dotted-path deleteField per compensation key being written to PII).
    if (piiData.compensation) {
      for (const compKey of Object.keys(piiData.compensation)) {
        basicData[`employment.${compKey}`] = deleteField();
      }
    }
  }

  const batch = writeBatch(db);

  // 1. Basic doc update (only if non-empty — empty basicData means a
  //    PII-only edit, which doesn't touch the basic doc beyond hasPii).
  if (Object.keys(basicData).length > 0) {
    batch.update(basicRef, basicData);
  }

  // 2. PII doc set-merge + hasPii flag on parent.
  if (Object.keys(piiData).length > 0) {
    batch.set(
      piiRef,
      {
        ...piiData,
        employeeId: empId,
        "metadata.updatedAt": serverTimestamp(),
      },
      { merge: true },
    );

    // Update parent's hasPii flag. Allowed by canPiiOnlyUpdate rule for
    // emp:pii:edit-only callers (parent doc only sees hasPii + metadata
    // change, no other fields).
    batch.update(basicRef, {
      hasPii: piiHasRequiredFields(piiData),
    });
  }

  // 3. Audit trail entry.
  if (auditEntry) {
    const auditRef = doc(collection(basicRef, "auditTrail"));
    batch.set(auditRef, {
      timestamp: serverTimestamp(),
      ...auditEntry,
    });
  }

  await batch.commit();
}
